import { FC, ReactElement, useRef } from 'react';

import { AzureDevopsTicketCreator } from '@evinced-private/ui-common';

import { JiraApi } from 'src/common/services/api/JiraApi';

import { ITicketsSystems } from './TicketsSystems.types';
import { useTicketsSystems } from './useTicketsSystems';

export interface ITicketsSystemsHeaderProps {
	showAddToJiraButton: boolean;
	onAddToJiraClick: () => void;
	showAddToAzureButton: boolean;
	onAddToAzureClick: () => void;
}

interface ITicketsSystemsHeaderActionsProps extends ITicketsSystems {
	children: (headerProps: ITicketsSystemsHeaderProps) => ReactElement;
}

/**
 * Hands EvIssueDetailsNew the props for its own "Add to Jira"/"Add to Azure" header buttons and
 * renders the ticket-creation modals they open.
 */
export const TicketsSystemsHeaderActions: FC<ITicketsSystemsHeaderActionsProps> = ({
	children,
	...ticketsSystemsProps
}) => {
	const {
		enableAzureIntegration,
		enableAzureBypassRule,
		azureConfig,
		azureTicket,
		isJiraConnected,
		exportToJira,
		renderJiraModal
	} = useTicketsSystems(ticketsSystemsProps);

	// AzureDevopsTicketCreator only opens from its own trigger, so the header's Azure button clicks a hidden one
	const azureTriggerRef = useRef<HTMLButtonElement>(null);

	const connectToJira = (): void => {
		window.open(JiraApi.getJiraAuthURL(), '_blank', 'noopener,noreferrer');
	};

	return (
		<>
			{children({
				showAddToJiraButton: !enableAzureIntegration,
				onAddToJiraClick: isJiraConnected ? exportToJira : connectToJira,
				showAddToAzureButton: enableAzureIntegration,
				onAddToAzureClick: () => azureTriggerRef.current?.click()
			})}
			{enableAzureIntegration && (
				<AzureDevopsTicketCreator
					config={azureConfig}
					ticket={azureTicket}
					bypassRules={enableAzureBypassRule}
					customTriggerComponent={
						<button type="button" aria-label="Add to Azure" hidden ref={azureTriggerRef} />
					}
				/>
			)}
			{renderJiraModal()}
		</>
	);
};
